/**
 * AccountantStageNav — the accountant Case Workspace navigation chrome:
 * the Case Profile Bar (claim-type / evidence chips) above the 8-stage Stage Strip.
 *
 * Self-contained: owns its own chip-selection state so the shell's hook surface is
 * unchanged. Rendered ONLY for role="accountant"; attorney/default keep WorkspaceTabBar.
 *
 * The profile is derived (via `deriveCaseProfile`) from the CT-0 `GET /case-profile`
 * SSoT, passed down by the shell as `caseProfile`. A server-confirmed posture renders
 * `attested`; an inferred/unconfirmed one renders plain `present`; an absent profile
 * (null) renders a hollow conservative default rather than asserting one.
 * Readiness pips default to 'unknown' until `derive_job_graph` is wired.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseProfileBar, type CaseProfileModel, type ProfileChipModel } from './case-profile';
import { StageStrip, stagesForRole, type StageStripItem } from './stage-strip';
import { postureLabel, modelLabel, type CaseProfilePayload } from '../shared/caseProfile';

export interface AccountantStageNavProps {
  /** Base path for stage navigation (e.g. /accountant/reports/:id) */
  basePath: string;
  /** The currently viewed stage id */
  activeStageId: string;
  /** The resolved case profile (CT-0 SSoT from GET /case-profile), or null when
   *  no valid v1.0 profile exists (legacy / 404 / fetch fail). */
  caseProfile?: CaseProfilePayload | null;
  className?: string;
}

/**
 * Derive the Case Profile Bar model from the CT-0 resolved profile (the
 * GET /case-profile SSoT the shell already holds). Honest status vocabulary:
 * a server-CONFIRMED posture is `attested` (✓); an inferred/unconfirmed one is
 * plain `present` (the working value, NOT verified). When no valid profile
 * exists (null), we do NOT assert a model — the conservative default renders
 * `hollow` ("assumed, not confirmed") instead of the old confidently-'present'
 * STANDARD BI.
 */
function deriveCaseProfile(p?: CaseProfilePayload | null): CaseProfileModel {
  if (!p) {
    return {
      typeChips: [{ id: 'standard_bi', label: 'STANDARD BI', status: 'hollow' }],
      evidenceChips: [],
    };
  }
  const typeChips: ProfileChipModel[] = [];
  if (p.posture) {
    typeChips.push({
      id: `posture_${p.posture}`,
      label: postureLabel(p.posture),
      status: p.posture_confirmed ? 'attested' : 'present',
    });
  }
  const model = p.damages_model;
  const knownModel = model === 'standard_bi' || model === 'future_closure';
  typeChips.push({
    id: model || 'model_unknown',
    label: knownModel ? modelLabel(model) : model ? model.toUpperCase() : 'MODEL —',
    status: knownModel ? 'present' : 'hollow',
  });
  const evidenceChips: ProfileChipModel[] = (p.pillars || []).map((pil) => ({
    id: `pillar_${pil}`,
    label: pil.toUpperCase(),
    status: 'present' as const,
  }));
  return { typeChips, evidenceChips };
}

export function AccountantStageNav({ basePath, activeStageId, caseProfile, className }: AccountantStageNavProps) {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const roleStages = stagesForRole('accountant');
  const activeIndex = roleStages.findIndex((s) => s.id === activeStageId);

  const stages: StageStripItem[] = roleStages.map((s, index) => ({
    id: s.id,
    label: s.label,
    // Chain-of-custody read: stages sealed before the active one render as
    // completed (hueless filled check, var(--color-verified)); the active
    // and pending stages keep the existing pip treatment untouched (K3 P1).
    pipState: activeIndex >= 0 && index < activeIndex ? 'attested' : undefined,
  }));

  const profile = deriveCaseProfile(caseProfile);

  return (
    <div className={className}>
      <CaseProfileBar
        profile={profile}
        selectedIds={selectedIds}
        onSelectedChange={setSelectedIds}
      />
      <StageStrip
        stages={stages}
        activeStageId={activeStageId}
        onNavigate={(id) => navigate(`${basePath}/${id}`)}
      />
    </div>
  );
}

export default AccountantStageNav;
